import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { FileText, Plus, Trash2, User as UserIcon, Pill } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MedicalNotesPanel = () => {
  const { t, language } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [form, setForm] = useState({
    patient_id: '',
    medications: '',
    dosage: '',
    notes: ''
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [notesRes, patientsRes] = await Promise.all([
        axios.get(`${API}/notes`, { withCredentials: true }),
        axios.get(`${API}/patients`, { withCredentials: true })
      ]);
      setNotes(notesRes.data);
      setPatients(patientsRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.patient_id || !form.notes.trim()) {
      setMsg({ type: 'error', text: t('يرجى اختيار مريض وكتابة الملاحظات', 'Please select a patient and write notes', 'Sélectionnez un patient et écrivez des notes') });
      return;
    }
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      await axios.post(`${API}/notes`, form, { withCredentials: true });
      setMsg({ type: 'success', text: t('تم حفظ الملاحظة', 'Note saved', 'Note enregistrée') });
      setForm({ patient_id: '', medications: '', dosage: '', notes: '' });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('حدث خطأ', 'An error occurred', 'Erreur') });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('حذف هذه الملاحظة؟', 'Delete this note?', 'Supprimer cette note?'))) return;
    try {
      await axios.delete(`${API}/notes/${id}`, { withCredentials: true });
      fetchAll();
    } catch (e) { /* noop */ }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#2c3e50]">
          {t('الملاحظات والوصفات', 'Notes & Prescriptions', 'Notes et ordonnances')}
        </h3>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="add-note-button">
          <Plus size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
          {showForm ? t('إلغاء', 'Cancel', 'Annuler') : t('إضافة ملاحظة', 'Add Note', 'Ajouter')}
        </Button>
      </div>

      {msg.text && (
        <Alert variant={msg.type === 'error' ? 'destructive' : 'default'} className={msg.type === 'success' ? 'bg-green-50 border-green-200' : ''}>
          <AlertDescription className={msg.type === 'success' ? 'text-green-800' : ''}>{msg.text}</AlertDescription>
        </Alert>
      )}

      {showForm && (
        <Card className="p-6" data-testid="note-form">
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>{t('المريض', 'Patient', 'Patient')} <span className="text-red-500">*</span></Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger className="mt-2" data-testid="patient-select">
                  <SelectValue placeholder={t('اختر مريض', 'Select patient', 'Sélectionnez un patient')} />
                </SelectTrigger>
                <SelectContent>
                  {patients.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">{t('لا يوجد مرضى', 'No patients yet', 'Aucun patient')}</div>
                  ) : patients.map(p => (
                    <SelectItem key={p._id} value={p._id}>{p.full_name} — {p.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>{t('الأدوية', 'Medications', 'Médicaments')}</Label>
              <Input value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })} placeholder={t('مثلاً: باراسيتامول', 'e.g. Paracetamol', 'ex: Paracétamol')} className="mt-2" data-testid="medications-input" />
            </div>

            <div>
              <Label>{t('الجرعة', 'Dosage', 'Posologie')}</Label>
              <Input value={form.dosage} onChange={(e) => setForm({ ...form, dosage: e.target.value })} placeholder={t('مثلاً: 500mg مرتين يومياً', 'e.g. 500mg twice daily', 'ex: 500mg 2x par jour')} className="mt-2" data-testid="dosage-input" />
            </div>

            <div>
              <Label>{t('الملاحظات', 'Notes', 'Notes')} <span className="text-red-500">*</span></Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} required className="mt-2" data-testid="notes-input" />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="save-note-button">
              {loading ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('حفظ الملاحظة', 'Save Note', 'Enregistrer')}
            </Button>
          </form>
        </Card>
      )}

      {notes.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-600">{t('لا توجد ملاحظات بعد', 'No notes yet', 'Aucune note pour le moment')}</p>
        </Card>
      ) : (
        notes.map((n) => (
          <Card key={n._id} className="p-4" data-testid={`note-${n._id}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <UserIcon size={16} className="text-[#1e88e5]" />
                  <span className="font-semibold">{n.patient_name}</span>
                  <span className="text-xs text-gray-500">• {new Date(n.created_at).toLocaleString()}</span>
                </div>
                {n.medications && (
                  <div className="text-sm mb-1 flex items-center gap-2">
                    <Pill size={14} className="text-purple-600" />
                    <span className="font-semibold">{t('الأدوية:', 'Medications:', 'Médicaments:')}</span> {n.medications}
                  </div>
                )}
                {n.dosage && (
                  <div className="text-sm mb-1">
                    <span className="font-semibold">{t('الجرعة:', 'Dosage:', 'Posologie:')}</span> {n.dosage}
                  </div>
                )}
                <p className="text-gray-700 text-sm mt-2 whitespace-pre-wrap">{n.notes}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(n._id)} data-testid={`delete-note-${n._id}`}>
                <Trash2 size={16} className="text-red-500" />
              </Button>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};
